/**
 * @fileoverview ChartSpec — single source of truth for agent-authored charts.
 *
 * Used by the createChart tool (as its inputSchema), the inline chat ChartCard,
 * and the canvas ChartNode. One definition, imported on both backend and
 * frontend (depends only on zod so it is safe to bundle in the browser).
 */
import { z } from "zod";

export const ChartSpecSchema = z.object({
  chartType: z.enum(["bar", "line", "area", "pie"]),
  title: z.string().min(1),
  description: z.string().optional(),
  /** Key into each data row for the category / x-axis. */
  xKey: z.string().min(1),
  /** One or more numeric series; `key` indexes into each data row. */
  series: z
    .array(z.object({ key: z.string().min(1), label: z.string().min(1) }))
    .min(1),
  /** Rows: each carries `xKey` plus each series `key`. */
  data: z.array(z.record(z.string(), z.union([z.string(), z.number()]))).min(1),
  /** bar/area only — stack series. */
  stacked: z.boolean().optional(),
});

export type ChartSpec = z.infer<typeof ChartSpecSchema>;
